// =============================================================================
// PREVIOUS IMPLEMENTATION — kept verbatim below for reference, intentionally
// inert. Line comments rather than a /* */ block: the original text contains
// comment terminators, which would close a block comment early.
//
// The active implementation follows after this block. Nothing above it is live
// code — it is here so the previous behaviour stays readable in place.
// =============================================================================

// import React, { ChangeEvent, DragEvent, FC, ReactNode, useState } from "react";
// import { useLocale } from "./i18n/locale";
//
// export const ACCEPTED_TYPES = ["application/pdf", "image/jpeg", "image/png"];
// export const MAX_SIZE_MB = 10;
//
// export interface DocumentDropzoneProps {
//   id: string;
//   /** Shown before a file is chosen; the filename replaces it afterwards. */
//   prompt: ReactNode;
//   /** Small print under the prompt, e.g. which documents are accepted. */
//   hint: ReactNode;
//   icon: ReactNode;
//   file: File | null;
//   onFileChange: (file: File | null) => void;
//   className?: string;
// }
//
// /**
//  * Shared upload control for the onboarding steps that take a document
//  * (step 2 identity, step 6 insurance). Validation lives here so both steps
//  * reject the same things the same way.
//  */
// const DocumentDropzone: FC<DocumentDropzoneProps> = ({
//   id,
//   prompt,
//   hint,
//   icon,
//   file,
//   onFileChange,
//   className = "",
// }) => {
//   const { t } = useLocale();
//   const [error, setError] = useState("");
//   const [isDragging, setIsDragging] = useState(false);
//
//   const acceptFile = (candidate?: File | null) => {
//     if (!candidate) return;
//
//     if (!ACCEPTED_TYPES.includes(candidate.type)) {
//       setError(t.dropzone.wrongType);
//       onFileChange(null);
//       return;
//     }
//     if (candidate.size > MAX_SIZE_MB * 1024 * 1024) {
//       setError(t.dropzone.tooLarge(MAX_SIZE_MB));
//       onFileChange(null);
//       return;
//     }
//
//     setError("");
//     onFileChange(candidate);
//   };
//
//   const handleDrop = (e: DragEvent<HTMLLabelElement>) => {
//     e.preventDefault();
//     setIsDragging(false);
//     acceptFile(e.dataTransfer.files?.[0]);
//   };
//
//   return (
//     <div className={className}>
//       <label
//         htmlFor={id}
//         onDragOver={(e) => {
//           e.preventDefault();
//           setIsDragging(true);
//         }}
//         onDragLeave={() => setIsDragging(false)}
//         onDrop={handleDrop}
//         className={`flex cursor-pointer flex-col items-center rounded-md border border-dashed bg-white px-6 pb-4 pt-2 text-center transition-colors focus-within:ring-2 focus-within:ring-primary-6000 ${
//           isDragging
//             ? "border-primary-6000 bg-cream-100"
//             : "border-neutral-300 hover:border-primary-6000"
//         }`}
//       >
//         {icon}
//
//         {/* break-all only for filenames — it would hyphenate the prompt mid-word. */}
//         <span
//           className={`text-[15px] font-semibold leading-snug text-navy-900 ${
//             file ? "break-all" : ""
//           }`}
//         >
//           {file ? file.name : prompt}
//         </span>
//
//         <span className="mt-3 text-xs leading-relaxed text-neutral-500">
//           {file ? t.dropzone.changeFile : hint}
//         </span>
//
//         <input
//           id={id}
//           type="file"
//           accept={ACCEPTED_TYPES.join(",")}
//           className="sr-only"
//           onChange={(e: ChangeEvent<HTMLInputElement>) =>
//             acceptFile(e.target.files?.[0])
//           }
//         />
//       </label>
//
//       {error && (
//         <p role="alert" className="mt-2 text-center text-xs text-red-600">
//           {error}
//         </p>
//       )}
//     </div>
//   );
// };
//
// export default DocumentDropzone;

// ==== ACTIVE IMPLEMENTATION ==================================================

import React, { ChangeEvent, DragEvent, FC, ReactNode, useState } from "react";
import { useLocale } from "./i18n/locale";

export const ACCEPTED_TYPES = ["application/pdf", "image/jpeg", "image/png"];
export const MAX_SIZE_MB = 10;

/**
 * "compact" is the phone steps' control, unchanged. "wide" is the desktop
 * workspace's: a tall box with a circular arrow, a serif prompt and a browse
 * link under it.
 */
export type DropzoneVariant = "compact" | "wide";

export interface DocumentDropzoneProps {
  id: string;
  /** Shown before a file is chosen; the filename replaces it afterwards. */
  prompt: ReactNode;
  /** Small print under the prompt, e.g. which formats are accepted. */
  hint: ReactNode;
  /** compact only — wide draws its own circular arrow. */
  icon?: ReactNode;
  file: File | null;
  onFileChange: (file: File | null) => void;
  variant?: DropzoneVariant;
  /** wide only: the lead-in before the browse link, e.g. "or ". */
  browsePrefix?: ReactNode;
  /** wide only: the underlined call to action, e.g. "browse your files". */
  browseLabel?: ReactNode;
  /** wide only: replaces the prompt once a file is accepted. */
  readyTitle?: ReactNode;
  /**
   * A document already held by the server, named so a tenant returning to the
   * step sees what they filed rather than an empty dropzone. `file` is a
   * freshly picked File and cannot survive a reload; this can.
   */
  savedFileName?: string;
  className?: string;
}

// The wide variant now draws its own disc inline, so it can swap the arrow for
// a tick and the cream fill for green once a file is accepted.
//
// /** The circular up-arrow the wide variant centres above its prompt. */
// const UploadBadge = () => (
//   <span className="flex h-14 w-14 items-center justify-center rounded-full bg-cream-100 text-navy-800">
//     <svg
//       className="h-5 w-5"
//       viewBox="0 0 24 24"
//       fill="none"
//       stroke="currentColor"
//       strokeWidth={1.6}
//       strokeLinecap="round"
//       strokeLinejoin="round"
//       aria-hidden="true"
//     >
//       <path d="M12 20V4m0 0l-6 6m6-6l6 6" />
//     </svg>
//   </span>
// );

/**
 * Shared upload control for the steps that take a document (identity,
 * insurance). Validation lives here so every caller rejects the same things the
 * same way; only the presentation branches on `variant`.
 */
const DocumentDropzone: FC<DocumentDropzoneProps> = ({
  id,
  prompt,
  hint,
  icon,
  file,
  onFileChange,
  variant = "compact",
  browsePrefix,
  browseLabel,
  readyTitle,
  savedFileName = "",
  className = "",
}) => {
  const { t } = useLocale();
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  // A just-picked file wins over one already on the server: the tenant is
  // replacing it, and the name under the tick has to be the one they chose.
  const shownName = file?.name || savedFileName;
  const hasDocument = Boolean(shownName);

  const acceptFile = (candidate?: File | null) => {
    if (!candidate) return;

    if (!ACCEPTED_TYPES.includes(candidate.type)) {
      setError(t.dropzone.wrongType);
      onFileChange(null);
      return;
    }
    if (candidate.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(t.dropzone.tooLarge(MAX_SIZE_MB));
      onFileChange(null);
      return;
    }

    setError("");
    onFileChange(candidate);
  };

  const handleDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragging(false);
    acceptFile(e.dataTransfer.files?.[0]);
  };

  const dragHandlers = {
    onDragOver: (e: DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      setIsDragging(true);
    },
    onDragLeave: () => setIsDragging(false),
    onDrop: handleDrop,
  };

  const input = (
    <input
      id={id}
      type="file"
      accept={ACCEPTED_TYPES.join(",")}
      className="sr-only"
      onChange={(e: ChangeEvent<HTMLInputElement>) =>
        acceptFile(e.target.files?.[0])
      }
    />
  );

  return (
    <div className={className}>
      {variant === "wide" ? (
        <label
          htmlFor={id}
          {...dragHandlers}
          className={`flex min-h-[240px] cursor-pointer flex-col items-center justify-center rounded-[6px] border-[1.5px] p-[30px] text-center transition-all min-[760px]:min-h-[270px] ${
            hasDocument
              ? "border-solid border-[#8ea795] bg-[#f8fbf8]"
              : isDragging
              ? "border-dashed border-gold bg-[#faf6ee]"
              : "border-dashed border-[#c5c1b8] bg-[#fcfbf8] hover:border-gold hover:bg-[#faf6ee]"
          } focus-within:ring-2 focus-within:ring-gold`}
        >
          <span
            className={`mb-4 grid h-[58px] w-[58px] place-items-center rounded-full text-[27px] ${
              hasDocument ? "bg-[#e5efe7] text-[#477454]" : "bg-[#f0ebe2] text-gold"
            }`}
            aria-hidden="true"
          >
            {hasDocument ? "✓" : "↑"}
          </span>

          <h3 className="mb-2 font-serif text-[20px] font-normal text-navy min-[760px]:text-[22px]">
            {hasDocument ? readyTitle : prompt}
          </h3>

          {hasDocument ? (
            // break-all only for filenames — it would hyphenate prose mid-word.
            <p className="m-0 break-all text-[13px] font-bold text-[#52685a] min-[760px]:text-[14px]">
              {shownName}
            </p>
          ) : (
            <>
              <p className="mb-3 text-[13px] text-[#7f8890] min-[760px]:text-[14px]">
                {browsePrefix}
                <span className="text-[#9b743c] underline">{browseLabel}</span>
              </p>
              <small className="text-[10px] text-[#a1a6aa] min-[760px]:text-[11px]">
                {hint}
              </small>
            </>
          )}

          {input}
        </label>
      ) : (
        <label
          htmlFor={id}
          {...dragHandlers}
          className={`flex cursor-pointer flex-col items-center rounded-md border border-dashed bg-white px-6 pb-4 pt-2 text-center transition-colors focus-within:ring-2 focus-within:ring-primary-6000 ${
            isDragging
              ? "border-primary-6000 bg-cream-100"
              : "border-neutral-300 hover:border-primary-6000"
          }`}
        >
          {icon}

          <span
            className={`text-[15px] font-semibold leading-snug text-navy-900 ${
              hasDocument ? "break-all" : ""
            }`}
          >
            {hasDocument ? shownName : prompt}
          </span>

          <span className="mt-3 text-xs leading-relaxed text-neutral-500">
            {hasDocument ? t.dropzone.changeFile : hint}
          </span>

          {input}
        </label>
      )}

      {error && (
        <p role="alert" className="mt-2 text-center text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

export default DocumentDropzone;
